<template>
  <div class="retro-data-annotations" v-if="image" ref="footerLock">
    <!-- Loading Overlay - only show when both image and data are loading -->
    <div v-if="isLoadingData || isImageLoading" class="loading-overlay">
      <div class="loading-content">
        <font-awesome-icon
          :icon="['fas', 'sync']"
          spin
          size="3x"
          class="mb-3"
        />
        <p class="loading-text">
          {{ isImageLoading ? "Loading Image..." : "Loading Annotations..." }}
        </p>
      </div>
    </div>

    <!-- Image Control Panel -->
    <ImageControlPanel
      :current-image-index="currentStudyIndex"
      :total-images="totalImages"
      :show-edit-labels="canEditLabels"
      @previous="handlePreviousImage"
      @next="handleNextImage"
      @close="handleClose"
      @edit-labels="handleEditLabels"
    />

    <div class="annotations-container" :class="{ loading: isLoadingData }">
      <!-- Main Image Section -->
      <div class="image-section" :style="{ width: imageSetColumnWidth }">
        <b-overlay
          :show="true"
          rounded="sm"
          shown="true"
          opacity="0.0"
          no-center
          class="boundingboxs"
        >
          <div ref="overlay" style="height: 100%; width: 0"></div>
          <img
            ref="annotatedImage"
            @load="imgLoaded"
            :src="imageUrl"
            class="main-image"
            :style="imageFilterStyle"
            loading="eager"
            decoding="async"
            fetchpriority="high"
          />
          <template #overlay>
            <canvas
              class="img-canvas"
              ref="canvas"
              :width="canvasWidth"
              :height="canvasHeight"
            />
          </template>
        </b-overlay>
      </div>

      <!-- Image Set Navigation Section -->
      <div
        v-if="isImageSet"
        class="image-set-section"
        :style="{ width: setColumnWidth }"
      >
        <!-- Window Selection Dropdown -->
        <div
          v-if="showWindowSelection && windowOptionsComputed.length > 1"
          class="window-selection mb-3"
        >
          <b-dropdown
            class="btn btn-sm btn-yellow"
            :text="selectedWindowDisplayed"
            variant="warn"
          >
            <b-dropdown-text
              v-for="win in windowOptionsComputed"
              :key="win"
              @click="selectWindow(win)"
            >
              {{ win }}
            </b-dropdown-text>
          </b-dropdown>
        </div>

        <!-- Image Set Container -->
        <div class="image-set-container">
          <div
            class="scrollable-thumbnails"
            :style="{ height: imageHeight + 'px' }"
            id="img_set"
          >
            <ul class="list-unstyled heatmap-list">
              <li
                v-for="_image in imageSetData"
                :key="_image.imgId"
                :id="_image.imgId"
                class="thumbnail-item"
                :class="
                  showWindowSelection && windowOptionsComputed.length > 1
                    ? 'mb-0'
                    : 'mb-1'
                "
              >
                <a href="#" @click.prevent="selectImage(_image.imgId)">
                  <img
                    :src="getImageUrl(_image.imgId) + '?thumb=Yes'"
                    class="img-fluid"
                    :class="{
                      active: selectedImageId === _image.imgId,
                      labelled: _image.labelled,
                    }"
                  />
                </a>
                <p
                  class="text-center mb-0 line-height-1"
                  :class="
                    selectedImageId === _image.imgId ? 'text-warning' : ''
                  "
                  v-if="showWindowSelection && windowOptionsComputed.length > 1"
                >
                  <small>{{ getImageIdentifier(_image.imgId) }}</small>
                </p>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <!-- Details Section -->
      <div class="details-section" :style="{ width: detailsColumnWidth }">
        <!-- Image Details Card -->
        <div class="detail-card">
          <div class="card-header">
            <h5 class="card-title">Image Details</h5>
          </div>
          <div class="card-content">
            <div class="detail-item">
              <span class="detail-label">Created on:</span>
              <span class="detail-value">{{
                formatDate(
                  selectedImageDetails.created ||
                    selectedImageDetails.created_at ||
                    image.created,
                  "yyyy-MM-dd HH:mm"
                )
              }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">Name:</span>
              <span class="detail-value">{{ imageName }}</span>
            </div>
            <div
              class="detail-item"
              v-if="
                imageIOU !== null &&
                imageIOU >= 0 &&
                annotators &&
                annotators.length > 0
              "
            >
              <span class="detail-label">Image / Slice IOV:</span>
              <span class="detail-value"
                >{{ (Math.round(imageIOU * 10000) / 100).toFixed(2) }}%</span
              >
            </div>
            <div
              class="detail-item"
              v-if="
                studyIOU !== null &&
                studyIOU >= 0 &&
                annotators &&
                annotators.length > 0 &&
                image.otherImages?.length > 0
              "
            >
              <span class="detail-label">Study IOV:</span>
              <span class="detail-value"
                >{{ (Math.round(studyIOU * 10000) / 100).toFixed(2) }}%</span
              >
            </div>
            <div class="detail-item" v-if="aiIOU !== null && aiIOU >= 0">
              <span class="detail-label">AI Accuracy:</span>
              <span class="detail-value"
                >{{ (Math.round(aiIOU * 10000) / 100).toFixed(2) }}%</span
              >
            </div>
            <div
              v-if="metadata && metadata.length > 0"
              class="metadata-section"
            >
              <a
                href="#"
                @click.prevent="metadataOpen = !metadataOpen"
                class="section-toggle"
              >
                <font-awesome-icon
                  :icon="[
                    'fas',
                    metadataOpen ? 'chevron-down' : 'chevron-right',
                  ]"
                  class="me-2"
                />
                <b>Metadata</b>
              </a>
              <div v-if="metadataOpen" class="metadata-list">
                <div v-for="(md, index) in metadata" :key="index">
                  <div
                    v-for="_md in md.values.filter((v) => v.value)"
                    :key="_md._id"
                    class="detail-item"
                  >
                    <span class="detail-label">{{ _md.name }}:</span>
                    <span class="detail-value">{{ _md.value }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Training Comments Card -->
        <div class="detail-card comments-card">
          <div class="card-header">
            <h5 class="card-title with-count">
              <a
                href="#"
                @click.prevent="commentsOpen = !commentsOpen"
                class="section-toggle"
              >
                <font-awesome-icon
                  :icon="
                    commentsOpen
                      ? ['fas', 'chevron-down']
                      : ['fas', 'chevron-right']
                  "
                  class="me-2"
                />
                Clinical Comments
              </a>
              <span v-if="sortedComments.length > 0" class="comment-count">
                {{ sortedComments.length }}
              </span>
            </h5>
          </div>
          <div v-if="commentsOpen" class="card-content">
            <!-- Comments List -->
            <div v-if="isLoadingComments" class="comments-loading">
              <font-awesome-icon :icon="['fas', 'sync']" spin class="me-2" />
              Loading comments...
            </div>

            <div v-else-if="sortedComments.length === 0" class="no-comments">
              <p class="text-muted mb-0">No comments yet.</p>
            </div>

            <div v-else class="comments-list">
              <!-- Expand button for older comments (shown before older comments when collapsed) -->
              <button
                v-if="sortedComments.length > 1 && !showAllComments"
                @click="showAllComments = true"
                class="expand-comments-btn"
              >
                <font-awesome-icon
                  :icon="['fas', 'chevron-down']"
                  class="me-2"
                />
                Show {{ sortedComments.length - 1 }} more {{ sortedComments.length - 1 === 1 ? 'comment' : 'comments' }}
              </button>

              <!-- Older comments (shown when expanded) -->
              <template v-if="showAllComments">
                <div
                  v-for="(comment, index) in sortedComments.slice(1)"
                  :key="'comment-' + comment.originalIndex"
                  class="comment-item older"
                >
                  <div class="comment-header">
                    <div class="comment-user">
                      <div class="user-info">
                        <span class="user-name">{{ comment.created_by?.personal?.fullname || 'Unknown' }}</span>
                        <span class="user-role">{{
                          getCommentUserRole(comment.created_by?._id)
                        }}</span>
                      </div>
                    </div>
                    <span class="comment-time">{{ formatCommentDate(comment.created_at) }}</span>
                  </div>
                  <p class="comment-message">{{ comment.message }}</p>
                  <div
                    v-if="canEditComment(comment)"
                    class="comment-actions"
                  >
                    <button @click="startEditing(comment, comment.originalIndex)" class="action-link">
                      <font-awesome-icon :icon="['fas', 'pen']" class="me-1" />
                      Edit
                    </button>
                  </div>
                </div>
              </template>

              <!-- Show less button (shown when expanded) -->
              <button
                v-if="sortedComments.length > 1 && showAllComments"
                @click="showAllComments = false"
                class="expand-comments-btn show-less"
              >
                <font-awesome-icon
                  :icon="['fas', 'chevron-up']"
                  class="me-2"
                />
                Show less
              </button>

              <!-- Latest comment (always visible) -->
              <div
                v-if="sortedComments.length > 0"
                class="comment-item latest"
                :class="{ 'editing': editingCommentIndex === latestCommentIndex }"
              >
                <div class="comment-header">
                  <div class="comment-user">
                    <div class="user-info">
                      <span class="user-name">{{ latestComment?.created_by?.personal?.fullname || 'Unknown' }}</span>
                      <span class="user-role">{{
                        getCommentUserRole(latestComment?.created_by?._id)
                      }}</span>
                    </div>
                  </div>
                  <span class="comment-time latest">{{
                    formatCommentDate(latestComment?.created_at)
                  }}</span>
                </div>

                <!-- View mode -->
                <div v-if="editingCommentIndex !== latestCommentIndex" class="comment-body">
                  <p class="comment-message">{{ latestComment?.message }}</p>
                  <div
                    v-if="canEditComment(latestComment)"
                    class="comment-actions"
                  >
                    <button @click="startEditing(latestComment, latestCommentIndex)" class="action-link">
                      <font-awesome-icon :icon="['fas', 'pen']" class="me-1" />
                      Edit
                    </button>
                    <button @click="confirmDeleteComment(latestCommentIndex)" class="action-link delete">
                      <font-awesome-icon :icon="['fas', 'trash']" class="me-1" />
                      Delete
                    </button>
                  </div>
                </div>

                <!-- Edit mode -->
                <div v-else class="comment-edit-mode">
                  <textarea
                    v-model="editingCommentText"
                    class="comment-textarea"
                    rows="3"
                    maxlength="5000"
                    @keydown.ctrl.enter="saveEditedComment(latestCommentIndex)"
                  />
                  <div class="edit-actions">
                    <small class="char-count">{{ editingCommentText.length }} / 5000</small>
                    <div class="action-buttons">
                      <button @click="cancelEdit" class="btn-cancel">Cancel</button>
                      <button
                        @click="saveEditedComment(latestCommentIndex)"
                        class="btn-save"
                        :disabled="!editingCommentText.trim() || isSavingComment"
                      >
                        <font-awesome-icon
                          v-if="isSavingComment"
                          :icon="['fas', 'sync']"
                          spin
                          class="me-2"
                        />
                        Save
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Add Comment Form (Administrator only) -->
            <div
              v-if="currentUserRole === 'Administrator'"
              class="add-comment-section"
            >
              <textarea
                v-model="newCommentText"
                class="comment-textarea new-comment"
                rows="3"
                placeholder="Enter your clinical observation or feedback..."
                maxlength="5000"
                @keydown.ctrl.enter="submitComment"
              />
              <div class="add-comment-actions">
                <small class="char-count">{{ newCommentText.length }} / 5000</small>
                <button
                  @click="submitComment"
                  class="btn-submit-comment"
                  :disabled="!newCommentText.trim() || isSubmittingComment"
                >
                  <font-awesome-icon
                    v-if="isSubmittingComment"
                    :icon="['fas', 'sync']"
                    spin
                    class="me-2"
                  />
                  <font-awesome-icon v-else :icon="['fas', 'paper-plane']" class="me-2" />
                  Add Comment
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Extracted Report Card -->
        <div class="detail-card" v-if="extractedReport">
          <div class="card-header">
            <h5 class="card-title">
              <a
                href="#"
                @click.prevent="reportOpen = !reportOpen"
                class="section-toggle"
              >
                <font-awesome-icon
                  :icon="
                    reportOpen
                      ? ['fas', 'chevron-down']
                      : ['fas', 'chevron-right']
                  "
                  class="me-2"
                />
                Extracted Report
              </a>
            </h5>
          </div>
          <div v-if="reportOpen" class="card-content">
            <pre class="report-text mb-2">{{ extractedReport.report_text }}</pre>
            <small class="report-meta d-block mb-1">
              <font-awesome-icon :icon="['fas', 'file-medical']" class="me-1" />
              Ground truth and metadata extracted from this report
            </small>
            <small
              v-if="extractedReport.received_at"
              class="report-meta d-block"
            >
              Imported {{ formatDate(extractedReport.received_at) }}
              <span v-if="extractedReport.importer">by {{ extractedReport.importer }}</span>
            </small>
          </div>
        </div>

        <!-- Annotators Card -->
        <div class="detail-card" v-if="annotators && annotators.length > 0">
          <div class="card-header">
            <h5 class="card-title">Annotators</h5>
          </div>
          <div class="card-content annotations">
            <div
              v-if="isReaderWithHiddenAnnotations"
              class="alert alert-info mb-2"
            >
              <small>
                <font-awesome-icon
                  :icon="['fas', 'info-circle']"
                  class="me-1"
                />
                As a {{ currentUserRole }}, you can only view your own annotations.
              </small>
            </div>
            <ul class="annotator-list">
              <li
                v-for="user in filteredAnnotators"
                :key="user.id"
                class="annotator-item"
              >
                <div
                  class="annotator-header"
                  @click="toggleUserBoundingBoxes(user)"
                >
                  <div class="annotator-info">
                    <button
                      v-if="
                        user.boundingBoxes &&
                        user.boundingBoxes.length > 0 &&
                        user.boundingBoxes[0] !== 'Normal'
                      "
                      class="color-badge"
                      :style="{
                        backgroundColor: user.boundingBoxes[0].colour,
                        borderColor: user.boundingBoxes[0].colour,
                      }"
                    ></button>
                    <button v-else class="color-badge"></button>
                    <div class="annotator-info-text">
                      <span class="annotator-name">{{ user.name }}</span>
                      <span v-if="getAnnotatorRole(user.id)" class="annotator-role">{{ getAnnotatorRole(user.id) }}</span>
                    </div>
                  </div>
                  <button class="toggle-btn">
                    <font-awesome-icon
                      :icon="[
                        'fas',
                        user.showBoundingBoxes ? 'chevron-up' : 'chevron-down',
                      ]"
                    />
                  </button>
                </div>
                <div class="annotator-details">
                  <div class="detail-item">
                    <span class="detail-label">Diagnoses:</span>
                    <span class="detail-value">{{
                      user.diagnoses.length > 0
                        ? user.diagnoses.join(", ")
                        : "Normal"
                    }}</span>
                  </div>
                  <div class="detail-item" v-if="user.name !== 'AI'">
                    <span class="detail-label">Tags:</span>
                    <span class="detail-value">{{
                      user.tags.length > 0 ? user.tags.join(", ") : "None"
                    }}</span>
                  </div>
                </div>
                <div v-if="user.showBoundingBoxes" class="pathology-list">
                  <div
                    v-for="(boundingBox, $index) in user.boundingBoxes"
                    :key="$index"
                    class="pathology-item"
                  >
                    <div class="pathology-row">
                      <button
                        v-if="user.boundingBoxes[0] !== 'Normal'"
                        @click.stop="toggleBoundingBox(boundingBox)"
                        class="visibility-btn"
                      >
                        <font-awesome-icon
                          :icon="[
                            'fas',
                            boundingBox.showBoundingBox ? 'eye' : 'eye-slash',
                          ]"
                        />
                      </button>
                      <span class="pathology-name">
                        {{
                          user.name !== "AI"
                            ? boundingBox.pathology
                            : boundingBox.pathology +
                              " (AIC: " +
                              (
                                Math.round(boundingBox.confidence * 10000) / 100
                              ).toFixed(2) +
                              "%)"
                        }}
                      </span>
                      <a
                        v-if="boundingBox.stats && boundingBox.stats.length > 0"
                        href="#"
                        @click.prevent="toggleStats(user.id + $index)"
                        class="stats-link"
                      >
                        <small>
                          <strong>Stats</strong>
                          <font-awesome-icon
                            :icon="
                              metadataOpen
                                ? ['fas', 'chevron-down']
                                : ['fas', 'chevron-right']
                            "
                            class="me-2"
                          />
                        </small>
                      </a>
                    </div>
                    <div
                      v-if="
                        boundingBox.stats &&
                        boundingBox.stats.length > 0 &&
                        statsOpen[user.id + $index]
                      "
                      class="stats-content"
                    >
                      <span
                        v-for="plane in boundingBox.stats"
                        :key="$index + plane.view"
                      >
                        <small>
                          <b>{{ plane.view }}:</b> Area:
                          {{
                            plane.area.toLocaleString(undefined, {
                              maximumFractionDigits: 2,
                            }) + plane.areaUnit
                          }}, {{ plane.modalityUnit }} mean:
                          {{
                            plane.mean.toLocaleString(undefined, {
                              maximumFractionDigits: 2,
                            })
                          }} </small
                        ><br />
                      </span>
                    </div>
                  </div>
                </div>
              </li>
            </ul>
          </div>
        </div>

        <!-- Image Enhancement Card -->
        <div class="detail-card">
          <div class="card-header">
            <h5 class="card-title">
              <a
                href="#"
                @click.prevent="enhanceImageOpen = !enhanceImageOpen"
                class="section-toggle"
              >
                <font-awesome-icon
                  :icon="
                    enhanceImageOpen
                      ? ['fas', 'chevron-down']
                      : ['fas', 'chevron-right']
                  "
                  class="me-2"
                />
                Enhance Image
              </a>
            </h5>
          </div>
          <div v-if="enhanceImageOpen" class="card-content">
            <div class="enhancement-control">
              <div class="control-icon">
                <font-awesome-icon :icon="['fas', 'sun']" title="Brightness" />
              </div>
              <input
                v-model="brightness"
                type="range"
                min="0"
                max="200"
                class="enhancement-slider"
              />
              <span class="control-value">{{ brightness }}%</span>
            </div>
            <div class="enhancement-control">
              <div class="control-icon">
                <font-awesome-icon :icon="['fas', 'adjust']" title="Contrast" />
              </div>
              <input
                v-model="contrast"
                type="range"
                min="0"
                max="200"
                class="enhancement-slider"
              />
              <span class="control-value">{{ contrast }}%</span>
            </div>
            <div class="enhancement-control">
              <div class="control-icon">
                <font-awesome-icon :icon="['fas', 'tint']" title="Saturation" />
              </div>
              <input
                v-model="saturate"
                type="range"
                min="0"
                max="200"
                class="enhancement-slider"
              />
              <span class="control-value">{{ saturate }}%</span>
            </div>
            <div class="enhancement-control">
              <div class="control-icon">
                <font-awesome-icon :icon="['fas', 'camera']" title="Sepia" />
              </div>
              <input
                v-model="sepia"
                type="range"
                min="0"
                max="100"
                class="enhancement-slider"
              />
              <span class="control-value">{{ sepia }}%</span>
            </div>
            <div class="enhancement-control">
              <div class="control-icon">
                <font-awesome-icon :icon="['fas', 'fill']" title="Invert" />
              </div>
              <input
                v-model="invert"
                type="range"
                min="0"
                max="100"
                class="enhancement-slider"
              />
              <span class="control-value">{{ invert }}%</span>
            </div>
          </div>
        </div>

        <!-- Image Rejection Card (Admin/Data Scientist only) -->
        <div class="detail-card" v-if="canRejectImage">
          <div class="card-header">
            <h5 class="card-title">Reject Image</h5>
          </div>
          <div class="card-content">
            <div class="detail-item">
              <input
                v-model="rejectReason"
                placeholder="Reject Image Reason"
                class="form-control"
                :class="{
                  'is-valid': rejectReason !== '',
                  'is-invalid': rejectReason === '',
                }"
              />
            </div>
            <div class="d-flex justify-content-around gap-2">
              <button
                class="btn btn-yellow rounded-pill flex-1"
                :disabled="rejectReason === ''"
                @click="rejectImage"
              >
                Reject
              </button>
              <button
                class="btn btn-blue rounded-pill flex-1"
                @click="rejectReason = ''"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import {
  ref,
  computed,
  watch,
  onMounted,
  onBeforeUnmount,
  nextTick,
} from "vue";
import { useStore } from "vuex";
import { apiRoute } from "@/helpers/api-route";
import { useDateFormat } from "@/composables/useDateFormat";
import { canLabel } from "@/helpers/project-management/roleCheck";
import { IOVThesholds } from "@/helpers/iovthresholds";
import { fetchWrapper } from "@/helpers/fetch-wrapper";
import ImageControlPanel from "@/modules/workspace/components/Shared/ImageControlPanel.vue";

const props = defineProps({
  image: {
    type: Object,
    required: true,
  },
  project: {
    type: Object,
    default: null,
  },
  imageSet: {
    type: Array,
    default: () => [],
  },
  windowOptions: {
    type: Array,
    default: () => [],
  },
  clientWidth: {
    type: Number,
    default: 1920,
  },
  phaseData: {
    type: Object,
    default: null,
  },
  currentStudyIndex: {
    type: Number,
    default: 0,
  },
  totalImages: {
    type: Number,
    default: 0,
  },
});

const emit = defineEmits(["closeBottom", "navigatePrevious", "navigateNext"]);

const store = useStore();
const { formatDate } = useDateFormat();

// Refs
const overlay = ref(null);
const annotatedImage = ref(null);
const canvas = ref(null);
const footerLock = ref(null);
const resizeObserver = ref(null);

// Data
const canvasWidth = ref(0);
const canvasHeight = ref(0);
const ratio = ref(1);
const brightness = ref(100);
const contrast = ref(100);
const saturate = ref(100);
const sepia = ref(0);
const invert = ref(0);
const metadataOpen = ref(false);
const enhanceImageOpen = ref(false);
const statsOpen = ref({});
const annotators = ref([]);
const labelledImageData = ref(null);
const isLoadingData = ref(false);
const isImageLoading = ref(true);

// Image Set Navigation
const currentImageIndex = ref(0);
const selectedImageId = ref(null);
const imageHeight = ref(700);
const selectedWindow = ref("");
const selectedWindowDisplayed = ref("");
const imageSetData = ref([]); // Internally managed image set
const previousImageUrl = ref(""); // Track previous image URL to detect actual changes

// Image Rejection
const rejectReason = ref("");

// Training Comments
const comments = ref([]);
const commentsOpen = ref(false);
const isLoadingComments = ref(false);
const showAllComments = ref(false);
const newCommentText = ref("");
const isSubmittingComment = ref(false);
const reportOpen = ref(false);
const editingCommentIndex = ref(null);
const editingCommentText = ref("");
const isSavingComment = ref(false);

// Get actual image dimensions from metadata (not PNG dimensions)
const getActualImageDimensions = () => {
  const details = selectedImageDetails.value
  if (!details) return null

  // Try to get from metadata "Dimensions" field (format: "3584x4608")
  const imageMetadata = details.metadata || []
  for (const md of imageMetadata) {
    if (!md.values) continue
    const dimensionsField = md.values.find(m => m.name === 'Dimensions')
    if (dimensionsField?.value) {
      const [width, height] = dimensionsField.value.split('x').map(Number)
      if (width && height) {
        return { width, height }
      }
    }
  }

  return null
}

// Computed
const imageUrl = computed(() => {
  // Use selectedImageId if available (for image set navigation), otherwise use props.image._id
  console.log("Selected Image ID:", selectedImageId.value);
  const imageId = selectedImageId.value || props.image?._id;
  if (!imageId) return null;
  return `${apiRoute()}/image/${imageId}`;
});

// Get the currently selected image details
const selectedImageDetails = computed(() => {
  if (selectedImageId.value && imageSetData.value.length > 0) {
    const selectedItem = imageSetData.value.find(
      (img) => img.imgId === selectedImageId.value
    );
    console.log("Selected Image Details:", selectedItem?.details || props.image);
    return selectedItem?.details || props.image;
  }
  return props.image;
});

const imageName = computed(() => {
  const details = selectedImageDetails.value;
  if (!details) return "Unknown";
  return (
    details.image_set?.name ||
    details.image?.name ||
    details.imgName ||
    "Untitled"
  );
});

const metadata = computed(() => {
  const details = selectedImageDetails.value;
  if (!details || !details.metadata || !props.project) return [];
  return details.metadata.filter((m) => m.project === props.project._id);
});

const extractedReport = computed(() => {
  const details = selectedImageDetails.value;
  if (!details || !details.reports || !props.project) return null;
  return (
    details.reports.find((r) => r.project === props.project._id) || null
  );
});

const imageIOU = computed(() => {
  const details = selectedImageDetails.value;
  console.log("Calculating Image IOV, details:", details?.iou, "labelledImageData:", labelledImageData.value?.accuracy);
  return 1 - (details?.iou ?? labelledImageData.value?.accuracy ?? null);
});

const studyIOU = computed(() => {
  const details = selectedImageDetails.value;
  console.log("Calculating Study IOV, details:", details, "labelledImageData:", labelledImageData.value);
  return 1 - (details?.aveIou ?? labelledImageData.value?.studyAccuracy ?? null);
});

const aiIOU = computed(() => {
  const details = selectedImageDetails.value;
  return details?.aiIov ?? labelledImageData.value?.aiAccuracy ?? null;
});

// Image Set computed properties
const isImageSet = computed(() => {
  return imageSetData.value && imageSetData.value.length > 0;
});

const showWindowSelection = computed(() => {
  if (!isImageSet.value || !imageSetData.value[0]) return false;
  const firstImage = imageSetData.value[0];
  const hasWindow = firstImage.details?.image_set?.window;
  const grouping = props.project?.extra?.grouping;
  return hasWindow && grouping !== "series" && grouping !== "sets";
});

const canRejectImage = computed(() => {
  return (
    currentUserRole.value === "Administrator" ||
    currentUserRole.value === "Data Scientist"
  );
});

const windowOptionsComputed = computed(() => {
  if (!imageSetData.value || imageSetData.value.length === 0) return [];

  // Extract unique window values from image set
  const windows = new Set();
  imageSetData.value.forEach((img) => {
    if (img.details?.image_set?.window) {
      windows.add(img.details.image_set.window);
    }
  });

  return Array.from(windows);
});

// Dynamic Layout - responsive column sizing
const setColumnWidth = computed(() => {
  if (!isImageSet.value) return "0%";
  if (props.clientWidth >= 1800) {
    return "calc(8.33% - 0.5rem)"; // 1 column (1/12)
  } else {
    return "calc(16.67% - 0.5rem)"; // 2 columns (2/12)
  }
});

const imageSetColumnWidth = computed(() => {
  if (!isImageSet.value) {
    return "calc(66.67% - 0.5rem)"; // 8/12 when no image set
  }
  if (props.clientWidth >= 1800) {
    return "calc(58.33% - 0.5rem)"; // 7/12
  } else {
    return "calc(58.33% - 0.5rem)"; // 7/12
  }
});

const detailsColumnWidth = computed(() => {
  if (!isImageSet.value) {
    return "calc(33.33% - 0.5rem)"; // 4/12 when no image set
  }
  if (props.clientWidth >= 1800) {
    return "calc(33.33% - 0.5rem)"; // 4/12 (remaining space)
  } else {
    return "calc(25% - 0.5rem)"; // 3/12
  }
});

const imageFilterStyle = computed(() => {
  return `width: 100%; filter: brightness(${brightness.value}%) contrast(${contrast.value}%) saturate(${saturate.value}%) sepia(${sepia.value}%) invert(${invert.value}%);`;
});

const user = computed(() => store.state.auth?.user);
const members = computed(() => store.state.usersSubmission?.members || []);

const currentUserRole = computed(() => {
  if (!user.value || !members.value || !props.project) return "View Only";

  const currentMember = members.value.find((m) => m._id === user.value.id);
  if (!currentMember) return "View Only";

  const projectRole = currentMember.projectRole?.find(
    (r) => r.project === props.project._id
  );
  return projectRole ? projectRole.role : "View Only";
});

const filteredAnnotators = computed(() => {
  if (currentUserRole.value === "Reader" || currentUserRole.value === "Moderator") {
    return annotators.value.filter((annotator) => {
      return (
        annotator.name === "AI" ||
        annotator.name === user.value?.name ||
        annotator.id === user.value?.id
      );
    });
  }
  return annotators.value;
});

const isReaderWithHiddenAnnotations = computed(() => {
  if (currentUserRole.value === "Reader" || currentUserRole.value === "Moderator") {
    const hiddenCount = annotators.value.length - filteredAnnotators.value.length;
    return hiddenCount > 0;
  }
  return false;
});

const projectId = computed(() => {
  return props.project?._id;
});

const canEditLabels = computed(() => {
  return canLabel(store, projectId.value);
});

// Comments computed properties
const currentImageId = computed(() => {
  return selectedImageId.value || props.image?._id;
});

const sortedComments = computed(() => {
  if (!comments.value || comments.value.length === 0) return [];
  // Sort by date descending (newest first)
  return [...comments.value]
    .map((comment, index) => ({ ...comment, originalIndex: index }))
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
});

const latestComment = computed(() => {
  return sortedComments.value[0] || null;
});

const latestCommentIndex = computed(() => {
  return latestComment.value?.originalIndex ?? null;
});

// Methods
const handleClose = () => {
  emit("closeBottom");
};

const handleEditLabels = () => {
  if (!props.image || !props.project) {
    console.warn("Cannot open labeler: missing image or project");
    return;
  }

  // Collapse the left panel when entering labeler mode
  store.dispatch("workspacePanel/setLeftPanelCollapsed", true);
  store.dispatch("workspacePanel/setRightPanelCollapsed", true);

  // Enter labeler mode with this specific image
  store.dispatch("workspacePanel/enterLabelerMode", {
    projectId: props.project._id,
    imageId: props.image._id,
  });
};

const handlePreviousImage = () => {
  emit("navigatePrevious");
};

const handleNextImage = () => {
  emit("navigateNext");
};

const recalculateCanvasDimensions = () => {
  if (!annotatedImage.value || !overlay.value) return;

  const img = annotatedImage.value;

  // Use ACTUAL image dimensions from metadata, not PNG naturalWidth/naturalHeight
  // This ensures coords are scaled correctly to match the original DICOM dimensions
  const actualDimensions = getActualImageDimensions();
  const originalWidth = actualDimensions?.width || img.naturalWidth;
  const originalHeight = actualDimensions?.height || img.naturalHeight;

  const containerWidth = img.clientWidth;
  const containerHeight = img.clientHeight;

  // Calculate the actual displayed size of the image with object-fit: contain
  // using the PNG's natural dimensions because the browser scales the PNG, not the DICOM.
  const imageAspect = img.naturalWidth / img.naturalHeight;
  const containerAspect = containerWidth / containerHeight;

  let displayedWidth, displayedHeight;

  if (imageAspect > containerAspect) {
    // Image is wider - width is the limiting factor
    displayedWidth = containerWidth;
    displayedHeight = containerWidth / imageAspect;
  } else {
    // Image is taller - height is the limiting factor
    displayedHeight = containerHeight;
    displayedWidth = containerHeight * imageAspect;
  }

  // The ratio for scaling annotations must use the DICOM dimensions (originalWidth)
  const newRatio = displayedWidth / originalWidth;

  // Only update if dimensions actually changed to avoid unnecessary redraws
  if (
    displayedWidth !== canvasWidth.value ||
    displayedHeight !== canvasHeight.value ||
    newRatio !== ratio.value
  ) {
    canvasWidth.value = Math.round(displayedWidth);
    canvasHeight.value = Math.round(displayedHeight);
    ratio.value = newRatio;

    // Redraw annotations with new dimensions
    nextTick(() => {
      drawImageAndBoxes();
    });
  }
};

const setupResizeObserver = () => {
  if (!annotatedImage.value) return;

  // Clean up existing observer if any
  if (resizeObserver.value) {
    resizeObserver.value.disconnect();
  }

  // Create new ResizeObserver to monitor image size changes
  resizeObserver.value = new ResizeObserver((entries) => {
    for (const entry of entries) {
      if (entry.target === annotatedImage.value) {
        recalculateCanvasDimensions();
      }
    }
  });

  // Start observing the image element
  resizeObserver.value.observe(annotatedImage.value);
};

// Image Set Navigation Methods
const selectImage = (imageId) => {
  selectedImageId.value = imageId;
  currentImageIndex.value = getSelectedIndex(imageId);
};

const onSliderChange = () => {
  // Blur the slider to prevent keyboard events from affecting it
  const slider = document.getElementById("image-scroll-bar");
  if (slider) {
    slider.blur();
    document.activeElement?.blur();
  }

  if (imageSetData.value && imageSetData.value[currentImageIndex.value]) {
    selectedImageId.value = imageSetData.value[currentImageIndex.value].imgId;
  }
};

const getSelectedIndex = (imageId) => {
  if (!imageSetData.value) return 0;
  return imageSetData.value.findIndex((img) => img.imgId === imageId);
};

const getImageIdentifier = (imageId) => {
  const imageItem = imageSetData.value?.find((img) => img.imgId === imageId);
  if (!imageItem) return "Unknown";
  return (
    imageItem.details?.image_set?.identifier ||
    imageItem.details?.image?.name ||
    "Unknown"
  );
};

const getImageUrl = (imageId) => {
  return `${apiRoute()}/image/${imageId}`;
};

// Window Selection
const selectWindow = (window) => {
  selectedWindow.value = window;
  const targetIndex = imageSetData.value.findIndex(
    (img) => img.details?.image_set?.window === window
  );
  if (targetIndex >= 0) {
    currentImageIndex.value = targetIndex;
    selectedImageId.value = imageSetData.value[targetIndex].imgId;
    document.activeElement?.blur();
  }
};

// Auto-scroll thumbnails to selected image
const scrollToSelectedImage = () => {
  if (!selectedImageId.value) return;

  nextTick(() => {
    const element = document.getElementById(selectedImageId.value);
    const container = document.getElementById("img_set");

    if (element && container) {
      const targetIndex = getSelectedIndex(selectedImageId.value);
      const thumbHeight = element.offsetHeight;
      const margin =
        parseInt(window.getComputedStyle(element).marginBottom) || 0;
      const desiredY =
        targetIndex === 0 ? 0 : (targetIndex - 1) * (thumbHeight + margin);

      container.scroll({
        top: desiredY,
        behavior: "smooth",
      });
    }
  });
};

// Keyboard Navigation
const keyScrollImg = (event) => {
  const slider = document.getElementById("image-scroll-bar");
  if (slider) {
    slider.blur();
  }

  if (!imageSetData.value || imageSetData.value.length === 0) return;

  const currentIndex = getSelectedIndex(selectedImageId.value);

  if (event.which === 38) {
    // Up arrow
    if (currentIndex > 0) {
      selectedImageId.value = imageSetData.value[currentIndex - 1].imgId;
      currentImageIndex.value = currentIndex - 1;
    }
  } else if (event.which === 40) {
    // Down arrow
    if (currentIndex < imageSetData.value.length - 1) {
      selectedImageId.value = imageSetData.value[currentIndex + 1].imgId;
      currentImageIndex.value = currentIndex + 1;
    }
  }
};

const preventKeyBoardScroll = (event) => {
  const keys = [33, 34, 35, 37, 38, 39, 40];
  if (keys.includes(event.keyCode)) {
    event.preventDefault();
    return false;
  }
};

// Image Rejection
const rejectImage = async () => {
  if (!rejectReason.value || !props.image || !props.project) return;

  try {
    const payload = {
      reason: rejectReason.value,
      p_id: props.project._id,
      removalIds: [props.image._id],
      phase: props.phaseData?.phase || 1,
    };

    await store.dispatch("projectSubmission/removeImageFromProject", payload);
    await store.dispatch(
      "labelledImagesSubmission/fetchLabelledImages2",
      props.project._id
    );

    // Reset and close
    rejectReason.value = "";
    emit("closeBottom");
  } catch (error) {
    console.error("Error rejecting image:", error);
  }
};

// Training Comments Methods
const loadComments = async () => {
  if (!currentImageId.value) return;

  isLoadingComments.value = true;
  try {
    const response = await fetchWrapper.get(
      `${apiRoute()}/api/v1/admin/training/comments/${currentImageId.value}`
    );
    comments.value = response || [];
    console.log("Loaded comments:", comments.value);
  } catch (error) {
    console.error("Error loading comments:", error);
    comments.value = [];
  } finally {
    isLoadingComments.value = false;
  }
};

const submitComment = async () => {
  if (!newCommentText.value.trim() || !currentImageId.value) return;

  isSubmittingComment.value = true;
  try {
    const response = await fetchWrapper.post(
      `${apiRoute()}/api/v1/admin/training/comments/${currentImageId.value}`,
      { message: newCommentText.value.trim() }
    );

    comments.value.push(response);
    newCommentText.value = "";

    // Show success feedback
    store.dispatch("alert/success", "Comment added successfully", { root: true });
  } catch (error) {
    console.error("Error submitting comment:", error);
    store.dispatch("alert/error", "Failed to add comment", { root: true });
  } finally {
    isSubmittingComment.value = false;
  }
};

const startEditing = (comment, index) => {
  editingCommentIndex.value = index;
  editingCommentText.value = comment.message;
};

const cancelEdit = () => {
  editingCommentIndex.value = null;
  editingCommentText.value = "";
};

const saveEditedComment = async (commentIndex) => {
  if (!editingCommentText.value.trim() || commentIndex === null) return;

  isSavingComment.value = true;
  try {
    const response = await fetchWrapper.put(
      `${apiRoute()}/api/v1/admin/training/comments/${currentImageId.value}/${commentIndex}`,
      { message: editingCommentText.value.trim() }
    );

    // Update the comment in the array
    if (comments.value[commentIndex]) {
      comments.value[commentIndex] = { ...comments.value[commentIndex], ...response };
    }

    cancelEdit();
    store.dispatch("alert/success", "Comment updated successfully", { root: true });
  } catch (error) {
    console.error("Error updating comment:", error);
    store.dispatch("alert/error", "Failed to update comment", { root: true });
  } finally {
    isSavingComment.value = false;
  }
};

const confirmDeleteComment = async (commentIndex) => {
  if (!confirm("Are you sure you want to delete this comment?")) return;

  try {
    await fetchWrapper.delete(
      `${apiRoute()}/api/v1/admin/training/comments/${currentImageId.value}/${commentIndex}`
    );

    // Remove the comment from the array
    comments.value.splice(commentIndex, 1);
    store.dispatch("alert/success", "Comment deleted successfully", { root: true });
  } catch (error) {
    console.error("Error deleting comment:", error);
    store.dispatch("alert/error", "Failed to delete comment", { root: true });
  }
};

const canEditComment = (comment) => {
  // Users can edit their own comments, Administrators can edit any
  if (currentUserRole.value === "Administrator") return true;
  return comment?.created_by?._id === user.value?.id;
};

const getUserInitials = (fullname) => {
  if (!fullname) return "?";
  const parts = fullname.trim().split(" ");
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return fullname.slice(0, 2).toUpperCase();
};

const getCommentUserRole = (userId) => {
  const member = members.value.find((m) => m._id === userId);
  if (!member) return "";

  const projectRole = member.projectRole?.find(
    (r) => r.project === props.project?._id
  );
  return projectRole?.role || "";
};

const getAnnotatorRole = (userId) => {
  if (!userId || userId === "ai") return "";
  const member = members.value.find((m) => m._id === userId);
  if (!member) return "";

  const projectRole = member.projectRole?.find(
    (r) => r.project === props.project?._id
  );
  return projectRole?.role || "";
};

const formatCommentDate = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;

  return formatDate(dateString, "dd MMM yyyy");
};

// Secondary findings stay off on open. A project with nothing flagged primary
// would otherwise render blank, so treat everything as primary in that case.
const isPrimaryPathology = (name) => {
  const pathologies = props.project?.pathologies || [];
  if (!pathologies.some((p) => p.primary)) return true;
  return pathologies.some((p) => p.label === name && p.primary);
};

// The AI also draws overlays that are not project pathologies at all, such as
// the apchest and heart measurements - only hide what the project calls
// secondary, so nothing of the AI's is lost to a name that does not match
const isSecondaryPathology = (name) =>
  (props.project?.pathologies || []).some(
    (p) => p.label === name && !p.primary
  );

// Read the same way the gallery reads study status: accuracy is agreement, so
// variability is its inverse. No figure at all counts as disagreement, which
// shows everything rather than hiding on the strength of missing data.
const isFullDisagreement = (variability) =>
  variability == null ||
  Number.isNaN(variability) ||
  variability >= IOVThesholds().partialAgreement;

// Which annotators speak for the ground truth, and which of their primary
// findings to show: moderators when the study was moderated, readers
// otherwise. A lone annotator speaks for themselves; several must agree,
// unless they fully disagree, when all their primary findings are shown.
const groundTruth = () => {
  const humans = annotators.value.filter((u) => u.name !== "AI");
  const moderators = humans.filter(
    (u) => getAnnotatorRole(u.id) === "Moderator"
  );
  const users = moderators.length > 0 ? moderators : humans;

  const perUser = users.map(
    (u) =>
      new Set(
        (u.boundingBoxes || [])
          .filter((b) => b?.pathology && isPrimaryPathology(b.pathology))
          .map((b) => b.pathology)
      )
  );
  const everything = new Set(perUser.flatMap((names) => [...names]));

  // Moderators are judged on their own agreement figure where the API supplies
  // one, since the overall accuracy blends them with the readers
  const variability =
    moderators.length > 0 && labelledImageData.value?.moderatorAccuracy != null
      ? 1 - labelledImageData.value.moderatorAccuracy
      : imageIOU.value;

  if (users.length <= 1 || isFullDisagreement(variability)) {
    return { users, pathologies: everything };
  }

  return {
    users,
    pathologies: new Set(
      [...everything].filter((name) => perUser.every((names) => names.has(name)))
    ),
  };
};

// Opening a study with every annotation drawn is unreadable, so start on the
// ground truth plus the AI and let the user bring in the rest.
const applyDefaultVisibility = () => {
  const { users, pathologies } = groundTruth();

  annotators.value.forEach((user) => {
    const isAI = user.name === "AI";
    const isGroundTruth = users.includes(user);

    user.showBoundingBoxes = isAI || isGroundTruth;
    (user.boundingBoxes || []).forEach((box) => {
      if (!box?.pathology) return;

      if (isAI) {
        box.showBoundingBox = !isSecondaryPathology(box.pathology);
        return;
      }

      // Annotators kept off open on their primary findings, so switching one
      // on shows something useful without dragging the secondaries in
      box.showBoundingBox = isGroundTruth
        ? isPrimaryPathology(box.pathology) && pathologies.has(box.pathology)
        : isPrimaryPathology(box.pathology);
    });
  });
};

const toggleUserBoundingBoxes = (user) => {
  user.showBoundingBoxes = !user.showBoundingBoxes;
  drawImageAndBoxes();
};

const toggleBoundingBox = (boundingBox) => {
  boundingBox.showBoundingBox = !boundingBox.showBoundingBox;
  drawImageAndBoxes();
};

const toggleStats = (key) => {
  statsOpen.value[key] = !statsOpen.value[key];
};

const imgLoaded = () => {
  console.log("imgLoaded called, isImageLoading:", isImageLoading.value);

  // Guard against being called when already not loading (prevents duplicate calls)
  if (!isImageLoading.value) {
    console.log("imgLoaded: already loaded, skipping");
    return;
  }

  if (!annotatedImage.value) {
    console.log("imgLoaded: no image element, skipping");
    return;
  }

  if (annotatedImage.value && isImageSet.value) {
    imageHeight.value = annotatedImage.value.height * 0.99;
  }

  // Set initial dimensions using the recalculation method
  recalculateCanvasDimensions();
  isImageLoading.value = false;
  console.log("imgLoaded: set isImageLoading to false");

  // Set up ResizeObserver to monitor future size changes
  setupResizeObserver();

  // Draw initial annotations
  nextTick(() => {
    drawImageAndBoxes();
  });
};

const drawImageAndBoxes = () => {
  if (!canvas.value) return;

  const ctx = canvas.value.getContext("2d");
  ctx.clearRect(0, 0, canvas.value.width, canvas.value.height);

  const usersToDisplay = filteredAnnotators.value;
  if (!usersToDisplay || usersToDisplay.length === 0) return;

  for (const user of usersToDisplay) {
    if (!user.showBoundingBoxes) continue;

    const isAI = user.name === "AI";

    for (const boundingBox of user.boundingBoxes) {
      if (!boundingBox.showBoundingBox) continue;

      // Special handling for AI annotations of specific pathologies
      if (isAI && boundingBox.coordinates && boundingBox.coordinates.x) {
        const pathologyLower = boundingBox.pathology.toLowerCase();
        if (pathologyLower === "apchest" || pathologyLower === "heart") {
          drawAISpecialBox(ctx, boundingBox);
          continue;
        }
      }

      // Draw polygon annotations
      if (boundingBox.polygon && boundingBox.polygon.length > 1) {
        drawPolygon(ctx, boundingBox, isAI);
      }
      // Draw rectangle annotations
      else if (boundingBox.coordinates && boundingBox.coordinates.x) {
        drawRectangle(ctx, boundingBox, isAI);
      }
    }
  }
};

const drawPolygon = (ctx, boundingBox, isAI = false) => {
  let minPoint = { x: canvasWidth.value, y: canvasHeight.value };

  ctx.lineWidth = 2;
  ctx.strokeStyle = boundingBox.colour;
  ctx.fillStyle = boundingBox.colour + "33";
  ctx.beginPath();
  ctx.moveTo(
    boundingBox.polygon[0].x * ratio.value,
    boundingBox.polygon[0].y * ratio.value
  );

  for (let j = 1; j < boundingBox.polygon.length; j++) {
    ctx.lineTo(
      boundingBox.polygon[j].x * ratio.value,
      boundingBox.polygon[j].y * ratio.value
    );
    if (boundingBox.polygon[j].x * ratio.value < minPoint.x) {
      minPoint = {
        x: boundingBox.polygon[j].x * ratio.value,
        y: boundingBox.polygon[j].y * ratio.value,
      };
    }
  }

  ctx.closePath();
  ctx.stroke();
  ctx.fill();

  // Draw label
  const fontSize = isAI
    ? Math.floor(canvasWidth.value * 0.009)
    : Math.floor(canvasWidth.value * (ratio.value > 1 ? 0.0125 : 0.0165));
  ctx.font = fontSize + 'px "Lato"';
  const title = ctx.measureText(boundingBox.pathology);

  ctx.shadowOffsetX = 0;
  ctx.shadowOffsetY = 0;
  ctx.shadowBlur = 0;

  const offsetX = title.width;
  const offsetY = isAI ? 20 * ratio.value : 30 * ratio.value;
  const startingX = boundingBox.window
    ? boundingBox.window.x * ratio.value
    : minPoint.x;
  const startingY = boundingBox.window
    ? (boundingBox.window.y + boundingBox.window.h / 2) * ratio.value
    : minPoint.y;
  let endingX = startingX - offsetX;
  if (endingX < title.width) {
    endingX = startingX + offsetX;
  }
  let endingY = startingY - offsetY;
  if (endingY < title.fontBoundingBoxAscent) {
    endingY = startingY + offsetY;
  }

  ctx.moveTo(startingX, startingY);
  ctx.lineTo(endingX, endingY);
  ctx.closePath();
  ctx.stroke();
  ctx.fillStyle = ctx.strokeStyle = boundingBox.colour;
  ctx.fillText(boundingBox.pathology, endingX, endingY);

  // Draw measurement lines if present
  if (
    boundingBox.other &&
    Array.isArray(boundingBox.other) &&
    boundingBox.other.length > 0
  ) {
    if (
      boundingBox.stats &&
      Array.isArray(boundingBox.stats) &&
      boundingBox.stats.length === 1
    ) {
      const lines = boundingBox.other.filter(
        (_line) => _line.view === boundingBox.stats[0].view
      );
      ctx.strokeStyle = boundingBox.colour;

      lines.forEach((_line) => {
        const _starting = { x: _line.points[0][0], y: _line.points[0][1] };
        const _ending = { x: _line.points[1][0], y: _line.points[1][1] };

        ctx.beginPath();
        ctx.moveTo(_starting.x * ratio.value, _starting.y * ratio.value);
        ctx.lineTo(_ending.x * ratio.value, _ending.y * ratio.value);
        ctx.closePath();
        ctx.stroke();
        ctx.beginPath();
        ctx.arc(
          _starting.x * ratio.value,
          _starting.y * ratio.value,
          3,
          0,
          2 * Math.PI,
          false
        );
        ctx.stroke();
        ctx.beginPath();
        ctx.arc(
          _ending.x * ratio.value,
          _ending.y * ratio.value,
          3,
          0,
          2 * Math.PI,
          false
        );
        ctx.stroke();

        if (_line.points.length === 4 && _line.type === "Bidirectional") {
          const __starting = { x: _line.points[2][0], y: _line.points[2][1] };
          const __ending = { x: _line.points[3][0], y: _line.points[3][1] };

          ctx.beginPath();
          ctx.moveTo(__starting.x * ratio.value, __starting.y * ratio.value);
          ctx.lineTo(__ending.x * ratio.value, __ending.y * ratio.value);
          ctx.closePath();
          ctx.stroke();
          ctx.beginPath();
          ctx.arc(
            __starting.x * ratio.value,
            __starting.y * ratio.value,
            3,
            0,
            2 * Math.PI,
            false
          );
          ctx.stroke();
          ctx.beginPath();
          ctx.arc(
            __ending.x * ratio.value,
            __ending.y * ratio.value,
            3,
            0,
            2 * Math.PI,
            false
          );
          ctx.stroke();
        }
      });
    } else {
      const lines = boundingBox.other.filter((_line) => _line.view === "Axial");
      ctx.strokeStyle = boundingBox.colour;
      lines.forEach((_line) => {
        ctx.beginPath();
        ctx.moveTo(
          _line.points[0][0] * ratio.value,
          _line.points[0][1] * ratio.value
        );
        ctx.lineTo(
          _line.points[1][0] * ratio.value,
          _line.points[1][1] * ratio.value
        );
        ctx.closePath();
        ctx.stroke();
        ctx.beginPath();
        ctx.arc(
          _line.points[0][0] * ratio.value,
          _line.points[0][1] * ratio.value,
          3,
          0,
          2 * Math.PI,
          false
        );
        ctx.stroke();
        ctx.beginPath();
        ctx.arc(
          _line.points[1][0] * ratio.value,
          _line.points[1][1] * ratio.value,
          3,
          0,
          2 * Math.PI,
          false
        );
        ctx.stroke();
      });
    }
  }
};

// Special AI drawing for apchest and heart pathologies
const drawAISpecialBox = (ctx, boundingBox) => {
  const colour = boundingBox.colour;
  const pathology = boundingBox.pathology.toUpperCase();

  ctx.shadowOffsetX = 2;
  ctx.shadowOffsetY = 2;
  ctx.shadowBlur = 4;
  ctx.shadowColor = colour;
  ctx.fillStyle = ctx.strokeStyle = colour;
  ctx.fillStyle = colour;

  ctx.lineWidth = 2;
  const fontSize = Math.floor(canvasWidth.value * 0.01367);
  ctx.font = fontSize + 'px "Lato"';
  const title = ctx.measureText(pathology);

  ctx.beginPath();
  ctx.moveTo(
    boundingBox.coordinates.x * ratio.value,
    boundingBox.coordinates.y * ratio.value +
      title.fontBoundingBoxAscent +
      title.fontBoundingBoxDescent
  );
  ctx.lineTo(
    boundingBox.coordinates.x * ratio.value,
    boundingBox.coordinates.y * ratio.value - title.fontBoundingBoxDescent
  );
  ctx.moveTo(
    boundingBox.coordinates.x * ratio.value +
      boundingBox.coordinates.w * ratio.value,
    boundingBox.coordinates.y * ratio.value +
      title.fontBoundingBoxAscent +
      title.fontBoundingBoxDescent
  );
  ctx.lineTo(
    boundingBox.coordinates.x * ratio.value +
      boundingBox.coordinates.w * ratio.value,
    boundingBox.coordinates.y * ratio.value - title.fontBoundingBoxDescent
  );
  ctx.moveTo(
    boundingBox.coordinates.x * ratio.value,
    boundingBox.coordinates.y * ratio.value
  );
  ctx.lineTo(
    boundingBox.coordinates.x * ratio.value +
      boundingBox.coordinates.w * ratio.value,
    boundingBox.coordinates.y * ratio.value
  );
  ctx.stroke();

  ctx.shadowOffsetX = 0;
  ctx.shadowOffsetY = 0;
  ctx.shadowBlur = 0;

  ctx.fillStyle = ctx.strokeStyle = colour;
  if (pathology.toLowerCase() === "apchest") {
    const newPathology =
      pathology +
      ", AIC: " +
      ((boundingBox.confidence || 0) * 100).toFixed(2) +
      "%";
    ctx.fillText(
      newPathology,
      boundingBox.coordinates.x * ratio.value + 5,
      boundingBox.coordinates.y * ratio.value + title.fontBoundingBoxAscent + 1,
      boundingBox.coordinates.w * ratio.value
    );
  } else if (pathology.toLowerCase() === "heart") {
    const newPathology = pathology + ", CTR";
    ctx.fillText(
      newPathology,
      boundingBox.coordinates.x * ratio.value + 5,
      boundingBox.coordinates.y * ratio.value + title.fontBoundingBoxAscent + 1,
      boundingBox.coordinates.w * ratio.value
    );
  } else {
    ctx.fillText(
      pathology,
      boundingBox.coordinates.x * ratio.value + 5,
      boundingBox.coordinates.y * ratio.value + title.fontBoundingBoxAscent - 1,
      boundingBox.coordinates.w * ratio.value
    );
  }
};

const drawRectangle = (ctx, boundingBox, isAI = false) => {
  ctx.strokeStyle = boundingBox.colour;
  ctx.lineWidth = 2;
  ctx.strokeRect(
    boundingBox.coordinates.x * ratio.value,
    boundingBox.coordinates.y * ratio.value,
    boundingBox.coordinates.w * ratio.value,
    boundingBox.coordinates.h * ratio.value
  );

  const fontSize = Math.floor(canvasWidth.value * 0.01367);
  ctx.font = fontSize + 'px "Lato"';
  const pathologyText =
    isAI && boundingBox.confidence
      ? boundingBox.pathology +
        " (AIC: " +
        (boundingBox.confidence * 100).toFixed(2) +
        "%)"
      : boundingBox.pathology;
  const title = ctx.measureText(pathologyText);

  ctx.fillStyle = boundingBox.colour;
  ctx.fillRect(
    boundingBox.coordinates.x * ratio.value,
    boundingBox.coordinates.y * ratio.value,
    boundingBox.coordinates.w * ratio.value,
    title.fontBoundingBoxAscent + title.fontBoundingBoxDescent
  );

  ctx.shadowOffsetX = 0;
  ctx.shadowOffsetY = 0;
  ctx.shadowBlur = 0;

  ctx.fillStyle = ctx.strokeStyle = "#ffffff";
  ctx.fillText(
    pathologyText,
    boundingBox.coordinates.x * ratio.value,
    boundingBox.coordinates.y * ratio.value + title.fontBoundingBoxAscent - 1,
    boundingBox.coordinates.w * ratio.value
  );

  // Draw window rectangle if present
  if (boundingBox.window && boundingBox.window.x) {
    ctx.strokeStyle = boundingBox.colour + "22";
    ctx.lineWidth = 2;
    ctx.strokeRect(
      boundingBox.window.x * ratio.value,
      boundingBox.window.y * ratio.value,
      boundingBox.window.w * ratio.value,
      boundingBox.window.h * ratio.value
    );
  }
};

const loadAnnotationData = async () => {
  if (!props.image || !props.project) {
    isLoadingData.value = false;
    return;
  }

  // Set loading state if not already set (watch may have already set it)
  if (!isLoadingData.value) {
    isLoadingData.value = true;
    annotators.value = [];
    labelledImageData.value = null;
  }

  // Always clear image set data to prevent stale data from previous image
  imageSetData.value = [];

  try {
    console.log("=== RetroDataAnnotations loadAnnotationData ===");
    console.log("props.image:", props.image);
    console.log(
      "props.image.otherImages length:",
      props.image.otherImages?.length
    );

    // Load labelled image data for this specific image using the single-image endpoint
    const payload = { projectId: props.project._id, imageID: props.image._id };
    await store.dispatch(
      "labelledImagesSubmission/fetchLabelledImages2Single",
      payload
    );

    const labelledImagesSingle =
      store.state.labelledImagesSubmission?.labelledImagesSingle || [];
    console.log("labelledImagesSingle:", labelledImagesSingle);
    console.log("labelledImagesSingle.length:", labelledImagesSingle.length);

    // Extract data from labelledImagesSingle if available
    let accuracy = null;
    let aiAccuracy = null;
    let avgAccuracy = 0;
    let imageData = null;
    const allImages = [];

    if (labelledImagesSingle.length > 0) {
      allImages.push(...labelledImagesSingle);

      labelledImagesSingle.forEach((img) => {
        if (img.otherImages && img.otherImages.length > 0) {
          allImages.push(...img.otherImages);
        }
      });

      imageData = allImages.find((li) => li.image._id === props.image._id);
      console.log("Found imageData:", imageData);

      if (imageData) {
        // Extract accuracy data (IOV)
        accuracy = imageData.image?.accuracy?.find(
          (ac) => ac.project === props.project._id
        );
        // Extract AI accuracy data (AI IOV)
        aiAccuracy = imageData.image?.ai_accuracy?.find(
          (ac) => ac.project === props.project._id
        );
        console.log("Extracted accuracy:", accuracy);
        console.log("Extracted AI accuracy:", aiAccuracy);
        // Extract average/study accuracy
        allImages.forEach((img) => {
          const accuracy = img.image?.accuracy?.find(
            (ac) => ac.project === props.project._id
          );
          avgAccuracy += accuracy?.value || 0;
        });
        avgAccuracy = avgAccuracy / allImages.length;
        console.log("Calculated average accuracy:", avgAccuracy);

        // Moderator-only agreement, where labels_single supplies it
        const moderatorAccuracy = imageData.image?.moderator_accuracy?.find(
          (ac) => ac.project === props.project._id
        );

        labelledImageData.value = {
          accuracy: accuracy?.value,
          aiAccuracy: aiAccuracy?.value,
          studyAccuracy: avgAccuracy,
          moderatorAccuracy: moderatorAccuracy?.value ?? null,
        };

        // Build annotators list using the same logic as getUserFromImage
        annotators.value = getUsersFromImageData(
          allImages,
          imageData.image._id,
          false,
          members.value
        );
        console.log("annotators.value:", annotators.value);
      }
    }

    // Build image set from otherImages - do this regardless of whether we have labelled data
    // Check both imageData.otherImages (from API) and props.image.otherImages (from props)
    const otherImagesSource = allImages.filter(
      (img) => img.image._id !== props.image._id
    );

    console.log("Checking for otherImages...");
    console.log("imageData?.otherImages:", imageData?.otherImages);
    console.log("props.image.otherImages:", props.image.otherImages);
    console.log("Selected source:", otherImagesSource);
    console.log("otherImagesSource length:", otherImagesSource?.length);

    if (otherImagesSource && otherImagesSource.length > 0) {
      // Build the image set array similar to bottomSheet module
      const setImages = [];

      // Add current image
      setImages.push({
        imgId: imageData.image._id,
        details: {
          ...imageData.image,
          image_set: imageData.image.image_set,
          iou: accuracy?.value || 0,
          aveIou: avgAccuracy?.value || null,
          aiIov: aiAccuracy?.value || null,
          imgName:
            imageData.image.image_set?.name || imageData.image.image?.name,
          created: imageData.image.created,
          otherImages: otherImagesSource,
        },
        boxes:
          labelledImagesSingle.length > 0
            ? getUsersFromImageData(
                allImages,
                imageData.image._id,
                false,
                members.value
              )
            : [],
        labelled: imageData?.pathologies && imageData.pathologies.length > 0,
      });

      // Add other images in the set
      otherImagesSource.forEach((otherImg) => {
        const otherImgId = otherImg.image?._id || otherImg._id;
        const otherLabelledData = allImages.find(
          (li) => li.image._id === otherImgId
        );

        setImages.push({
          imgId: otherImgId,
          details: {
            ...otherImg,
            image: otherImg.image,
            image_set: otherImg.image_set,
            imgName: otherImg.image?.image_set?.name,
            created: otherImg.image?.created_at,
          },
          boxes:
            labelledImagesSingle.length > 0
              ? getUsersFromImageData(
                  allImages,
                  otherImgId,
                  false,
                  members.value
                )
              : [],
          labelled:
            otherLabelledData &&
            otherLabelledData.pathologies &&
            otherLabelledData.pathologies.length > 0,
        });
      });

      imageSetData.value = setImages;
      console.log(
        "Built image set with",
        setImages.length,
        "images:",
        setImages
      );

      // Initialize selection if not already set
      if (!selectedImageId.value && setImages.length > 0) {
        const labelledImage = setImages.find((img) => img.labelled);
        selectedImageId.value = labelledImage?.imgId || setImages[0].imgId;
        currentImageIndex.value = getSelectedIndex(selectedImageId.value);

        // Set window display if available
        if (setImages[0].details?.image_set?.window) {
          selectedWindowDisplayed.value = setImages[0].details.image_set.window;
        }
      } else if (setImages.length > 0) {
        // Even if selectedImageId is already set, verify it exists in the new set
        const existsInSet = setImages.some((img) => img.imgId === selectedImageId.value);
        if (!existsInSet) {
          // Selected image doesn't exist in new set, select the first one
          const labelledImage = setImages.find((img) => img.labelled);
          selectedImageId.value = labelledImage?.imgId || setImages[0].imgId;
          currentImageIndex.value = getSelectedIndex(selectedImageId.value);
        }
      }
    } else {
      console.log("No otherImages found in imageData or props.image");
    }

    // Always try to load AI annotations - let the API return empty if none exist
    if (aiAccuracy && aiAccuracy.value >= 0) {
      console.log("Fetching AI annotations for image:", props.image._id);
      try {
        await store.dispatch(
          "labelledImagesSubmission/fetchAILabelledImagesSingle",
          payload
        );
        const aiImagesSingle =
          store.state.labelledImagesSubmission?.aiImagesSingle || [];
        console.log("AI annotations response:", aiImagesSingle);
        console.log("AI annotations length:", aiImagesSingle.length);

        if (aiImagesSingle.length > 0) {
          const aiAnnotators = getUsersFromImageData(
            aiImagesSingle,
            props.image._id,
            true,
            members.value
          );
          console.log("Processed AI annotators:", aiAnnotators);
          console.log("AI annotators count:", aiAnnotators.length);

          if (aiAnnotators.length > 0) {
            // Add AI annotators to the list
            annotators.value = [...annotators.value, ...aiAnnotators];
            console.log(
              "Total annotators after adding AI:",
              annotators.value.length
            );
          } else {
            console.log("No AI annotators found after processing");
          }
        } else {
          console.log("No AI annotations found in response");
        }
      } catch (error) {
        console.error("Error fetching AI annotations:", error);
        // Don't fail the whole load if AI fetch fails
      }
    }

    // Sort annotators by name
    annotators.value.sort((a, b) => a.name.localeCompare(b.name));

    applyDefaultVisibility();

    // Log final annotator state
    console.log("=== Final Annotators State ===");
    console.log("Total annotators:", annotators.value.length);
    annotators.value.forEach((annotator) => {
      console.log(
        `- ${annotator.name}: ${
          annotator.boundingBoxes?.length || 0
        } boxes, showBoundingBoxes: ${annotator.showBoundingBoxes}`
      );
    });
  } catch (error) {
    console.error("Error loading annotation data:", error);
  } finally {
    isLoadingData.value = false;
  }
};

// Helper function to extract user/AI data from image data (mirrors getUserFromImage from bottomSheet.module.js)
const getUsersFromImageData = (imageData, imgId, aiFlag, membersList) => {
  if (!Array.isArray(imageData)) {
    return [];
  }

  const users = [];
  const img = imageData.find((_img) => _img.image._id === imgId);

  if (!img || !img.pathologies) {
    return [];
  }

  const bbColours = [
    "#C77D3D",
    "#A4414A",
    "#7D1E5F",
    "#5C0F6B",
    "#3C0A6F",
    "#1E0A6E",
    "#0B0C63",
    "#0A1052",
    "#0C1441",
    "#13192F",
    "#1D1E1E",
    "#2A2213",
    "#3A2709",
    "#4B2C00",
    "#5D3100",
    "#6F3600",
    "#813B00",
    "#933F00",
    "#A54200",
    "#B74400",
  ];

  const pathologies = img.pathologies.sort((a, b) =>
    a.created_by.localeCompare(b.created_by)
  );

  pathologies.forEach((_p) => {
    const boundingBoxes = [];
    let colour = "#051135";

    if (!aiFlag) {
      colour = bbColours.shift() || "#051135";
    }

    const userMember = !aiFlag
      ? membersList.find((mem) => mem._id === _p.created_by)
      : { _id: "ai", personal: { fullname: "AI" } };

    if (!userMember) return;

    const indexes = Object.keys(_p).filter((key) => key !== "created_by");

    indexes.forEach((ind) => {
      const pathName = _p[ind].name;
      const coordinates = _p[ind].coords
        ? JSON.parse(JSON.stringify(_p[ind].coords))
        : null;
      const polygon = _p[ind].freehandpoints
        ? JSON.parse(JSON.stringify(_p[ind].freehandpoints))
        : null;
      const window = _p[ind].window_coords
        ? JSON.parse(JSON.stringify(_p[ind].window_coords))
        : coordinates;
      const other = _p[ind].other
        ? JSON.parse(JSON.stringify(_p[ind].other))
        : null;
      const stats = [];

      if (_p[ind].mapped_stats) {
        for (const prop in _p[ind].mapped_stats) {
          if (_p[ind].mapped_stats[prop]) {
            stats.push(_p[ind].mapped_stats[prop]);
          }
        }
      }

      if (!aiFlag) {
        boundingBoxes.push({
          colour,
          coordinates,
          polygon,
          window,
          other,
          stats,
          pathology: pathName,
          showBoundingBox: true,
        });
      } else {
        boundingBoxes.push({
          colour,
          coordinates,
          polygon,
          window,
          other,
          stats,
          pathology: pathName,
          showBoundingBox: true,
          confidence: _p[ind].confidence || 0,
        });
      }
    });

    // Extract tags
    const tags = [];
    if (img.tags && img.tags.find((d) => d.created_by === _p.created_by)) {
      const tempTags = img.tags.find((d) => d.created_by === _p.created_by);
      const keyst = Object.keys(tempTags);
      keyst.forEach((_k) => {
        if (_k !== "created_by") {
          tags.push(tempTags[_k]);
        }
      });
    }

    // Extract diagnoses
    const diagnoses = [];
    if (
      img.diagnoses &&
      img.diagnoses.find((d) => d.created_by === _p.created_by)
    ) {
      const tempDias = img.diagnoses.find(
        (d) => d.created_by === _p.created_by
      );
      const keys = Object.keys(tempDias);
      keys.forEach((_k) => {
        if (_k !== "created_by") {
          diagnoses.push(tempDias[_k]);
        }
      });
    }

    users.push({
      id: userMember._id,
      name: userMember.personal.fullname,
      boundingBoxes,
      showBoundingBoxes: true,
      tags,
      diagnoses,
    });
  });

  return users;
};

// Lifecycle
onMounted(async () => {
  // Load members for role checking
  if (props.project) {
    await store.dispatch("usersSubmission/getMembers", props.project._id);
  }

  // Load annotation data (this will populate imageSetData and initialize selection)
  await loadAnnotationData();

  // Set image height from annotated image after loading
  if (annotatedImage.value && isImageSet.value) {
    imageHeight.value = annotatedImage.value.height * 0.99;
  }

  // Add keyboard event listeners
  window.addEventListener("keyup", keyScrollImg);
  document.addEventListener("keydown", preventKeyBoardScroll, false);
});

onBeforeUnmount(() => {
  // Remove keyboard event listeners
  window.removeEventListener("keyup", keyScrollImg);
  document.removeEventListener("keydown", preventKeyBoardScroll, false);

  // Clean up ResizeObserver
  if (resizeObserver.value) {
    resizeObserver.value.disconnect();
    resizeObserver.value = null;
  }
});

// Watch for image changes
watch(
  () => props.image,
  async (newImage, oldImage) => {
    if (newImage && newImage._id !== oldImage?._id) {
      console.log("=== Image changed, loading new study ===");

      // Immediately set loading states and clear old data to prevent showing stale annotations
      isLoadingData.value = true;
      isImageLoading.value = true;
      annotators.value = [];
      labelledImageData.value = null;
      selectedImageId.value = null; // Reset selected image to force use of new props.image
      previousImageUrl.value = ""; // Reset URL tracking

      // Clean up existing ResizeObserver
      if (resizeObserver.value) {
        resizeObserver.value.disconnect();
        resizeObserver.value = null;
      }

      // Clear canvas
      if (canvas.value) {
        const ctx = canvas.value.getContext("2d");
        ctx.clearRect(0, 0, canvas.value.width, canvas.value.height);
      }

      // Load new annotation data
      await loadAnnotationData();

      // Use requestAnimationFrame to check if image is already loaded
      // This gives the browser a chance to update the DOM and start loading the image
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (isImageLoading.value &&
              annotatedImage.value &&
              annotatedImage.value.complete &&
              annotatedImage.value.naturalHeight !== 0) {
            console.log("Image was cached (RAF), calling imgLoaded from image watcher");
            imgLoaded();
          } else {
            console.log("Image not cached, waiting for @load event. isImageLoading:", isImageLoading.value);
          }
        });
      });
    }
  },
  { immediate: false }
);

// Watch for selected image changes
watch(selectedImageId, async (newImageId, oldImageId) => {
  if (newImageId && newImageId !== oldImageId) {
    console.log("Selected image changed to:", newImageId);

    // Find the selected image in the image set
    const selectedImageItem = imageSetData.value?.find(
      (img) => img.imgId === newImageId
    );
    console.log("Selected image item:", selectedImageItem);

    if (selectedImageItem) {
      // Update annotators to match the selected image (like the original component)
      annotators.value =
        selectedImageItem.boxes?.sort((a, b) => a.name.localeCompare(b.name)) ||
        [];
      applyDefaultVisibility();

      // Update window display if available
      if (selectedImageItem.details?.image_set?.window) {
        selectedWindowDisplayed.value =
          selectedImageItem.details.image_set.window;
        selectedWindow.value = "";
      }

      console.log(
        "Updated annotators for image:",
        newImageId,
        "Count:",
        annotators.value.length
      );
    }

    // Auto-scroll to selected image in thumbnail list
    scrollToSelectedImage();

    // Set loading state
    isImageLoading.value = true;

    // Use requestAnimationFrame to ensure the image src has been updated
    // and check if it's already loaded from cache
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        // Double RAF to ensure browser has processed the src change
        if (annotatedImage.value &&
            annotatedImage.value.complete &&
            annotatedImage.value.naturalHeight !== 0 &&
            isImageLoading.value) {
          console.log("Image already loaded from cache (RAF), calling imgLoaded");
          imgLoaded();
        } else {
          console.log("Image not cached, waiting for @load event");
        }
      });
    });
  }
});

// Watch for slider index changes
watch(currentImageIndex, (newIndex) => {
  const slider = document.getElementById("image-scroll-bar");
  if (slider) {
    slider.blur();
    document.activeElement?.blur();
  }

  if (
    imageSetData.value &&
    newIndex !== undefined &&
    getSelectedIndex(selectedImageId.value) !== newIndex
  ) {
    selectedImageId.value = imageSetData.value[newIndex].imgId;
  }
});

// Watch for window changes
watch(selectedWindow, (newWindow, oldWindow) => {
  if (newWindow !== "" && newWindow !== oldWindow) {
    const targetIndex = imageSetData.value.findIndex(
      (img) => img.details?.image_set?.window === newWindow
    );
    if (targetIndex >= 0) {
      currentImageIndex.value = targetIndex;
      selectedImageId.value = imageSetData.value[targetIndex].imgId;
      document.activeElement?.blur();
    }
  }
});

// Watch for project selection removed, i.e., Home button clicked
watch(projectId, () => {
  if (!projectId.value) {
    handleClose();
  }
});

// Watch for image ID changes to load comments
watch(
  currentImageId,
  (newImageId, oldImageId) => {
    if (newImageId && newImageId !== oldImageId) {
      console.log("Image ID changed, loading comments for:", newImageId);
      loadComments();
    }
  },
  { immediate: true }
);

// Watch for annotators changes and redraw annotations
watch(
  annotators,
  () => {
    // When annotators data loads or changes, redraw the canvas
    // This ensures annotations are visible even if data loads after the image
    nextTick(() => {
      drawImageAndBoxes();
    });
  },
  { deep: true }
);
</script>

<style scoped>
.retro-data-annotations {
  min-height: calc(100vh - var(--header-height));
  background-color: var(--color-bg-secondary);
  padding: 1rem;
  position: relative;
  overflow: hidden;
}

.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(var(--color-bg-primary-rgb, 8, 22, 36), 0.95);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(4px);
}

.loading-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: var(--color-accent-primary);
}

.loading-text {
  color: var(--color-text-primary);
  font-size: 1.2rem;
  font-weight: var(--font-weight-semibold);
  margin: 0;
}

.annotations-container.loading {
  opacity: 0.5;
  pointer-events: none;
}

.annotations-container {
  display: flex;
  gap: 1rem;
  height: calc(100vh - var(--header-height) - 2rem);
  max-height: calc(100vh - var(--header-height) - 2rem);
  overflow: hidden;
}

.image-section {
  background-color: #000000;
  border: var(--border-width-card) solid var(--color-border-secondary);
  border-radius: var(--border-radius-standard);
  padding: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: auto;
  max-height: 100%;
}

.boundingboxs {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.main-image {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  display: block;
}

.img-canvas {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  pointer-events: none;
}

.expand-link {
  color: var(--color-text-primary);
  text-decoration: none;
  display: inline-flex;
  align-items: center;
}

.expand-link:hover {
  color: var(--color-accent-primary);
}

.metadata-content {
  margin-top: 0.5rem;
  padding-left: 1.5rem;
}

.alert {
  background-color: rgba(59, 130, 246, 0.1);
  border-left: 3px solid #3b82f6;
  padding: 0.5rem;
  border-radius: var(--border-radius-small);
  color: var(--color-text-primary);
}

.enhance-controls {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-top: 0.75rem;
}

.control-group {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.control-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--color-text-primary);
  font-size: 0.9rem;
}

.control-slider {
  width: 100%;
  height: 6px;
  border-radius: 3px;
  background: var(--color-bg-primary);
  outline: none;
  opacity: 0.9;
  transition: opacity 0.2s;
  appearance: none;
  -webkit-appearance: none;
}

.control-slider:hover {
  opacity: 1;
}

.control-slider::-webkit-slider-thumb {
  appearance: none;
  -webkit-appearance: none;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: var(--color-accent-primary);
  cursor: pointer;
}

.control-slider::-moz-range-thumb {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: var(--color-accent-primary);
  cursor: pointer;
  border: none;
}

/* Custom scrollbar for image section */
.image-section::-webkit-scrollbar {
  width: var(--scrollbar-width);
  height: var(--scrollbar-width);
}

.image-section::-webkit-scrollbar-track {
  background: rgba(0, 0, 0, 0.3);
}

.image-section::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.2);
  border-radius: var(--border-radius-small);
}

.image-section::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 255, 255, 0.3);
}

/* Form Controls */
.form-control {
  background-color: var(--color-bg-primary);
  border: var(--border-width-standard) solid var(--color-border-secondary);
  border-radius: var(--border-radius-small);
  color: var(--color-text-primary);
  padding: 0.5rem 0.75rem;
  font-size: 0.9rem;
  width: 100%;
}

.form-control:focus {
  outline: none;
  border-color: var(--color-accent-primary);
  box-shadow: 0 0 0 2px rgba(255, 193, 7, 0.2);
}

.form-control.is-valid {
  border-color: #28a745;
}

.form-control.is-invalid {
  border-color: var(--color-border-secondary);
}

.btn {
  padding: 0rem 0.25rem;
  border: none;
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: var(--font-weight-semibold);
  transition: all 0.2s ease;
}

.btn-yellow {
  background-color: var(--color-accent-primary);
  color: var(--color-bg-primary);
}

.btn-yellow:hover:not(:disabled) {
  background-color: var(--color-accent-hover);
}

.btn-yellow:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-blue {
  background-color: #3b82f6;
  color: white;
}

.btn-blue:hover {
  background-color: #2563eb;
}

.rounded-pill {
  border-radius: 50rem;
}

.flex-1 {
  flex: 1;
}

.gap-2 {
  gap: 0.5rem;
}

/* Image Set Section */
.image-set-section {
  display: flex;
  flex-direction: column;
  background-color: var(--color-bg-secondary);
  border: var(--border-width-card) solid var(--color-border-secondary);
  border-radius: var(--border-radius-standard);
  padding: 1rem 0.25rem;
  overflow: hidden;
}

.window-selection {
  display: flex;
  justify-content: center;
}

.image-set-container {
  display: flex;
  align-items: stretch;
  overflow: hidden;
  flex: 1;
  min-height: 400px;
}

.scrollable-thumbnails {
  overflow-y: auto;
  overflow-x: hidden;
  flex: 1;
}

.scrollable-thumbnails::-webkit-scrollbar {
  width: var(--scrollbar-width);
}

.scrollable-thumbnails::-webkit-scrollbar-track {
  background: var(--color-scrollbar-track);
}

.scrollable-thumbnails::-webkit-scrollbar-thumb {
  background: var(--color-scrollbar-thumb);
  border-radius: var(--border-radius-small);
}

.scrollable-thumbnails::-webkit-scrollbar-thumb:hover {
  background: var(--color-scrollbar-thumb-hover);
}

.heatmap-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.thumbnail-item {
  margin-bottom: 1rem;
  padding: 0 0.25rem;
}

.thumbnail-item img {
  border: 3px solid transparent;
  border-radius: var(--border-radius-small);
  transition: all 0.2s ease;
  cursor: pointer;
}

.thumbnail-item img:hover {
  border-color: var(--color-accent-secondary);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
}

.thumbnail-item img.active {
  border-color: var(--color-accent-primary);
  box-shadow: 0 3px 12px rgba(255, 193, 7, 0.4);
}

.thumbnail-item img.labelled {
  border: 3px solid #28a745;
  box-shadow: 0 5px 10px 5px rgba(0, 0, 0, 0.2);
}

.thumbnail-item .text-warning {
  color: var(--color-accent-primary) !important;
  font-weight: var(--font-weight-bold);
}

/* Enhancement Controls */
.enhancement-control {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.75rem 0;
  border-bottom: 1px solid var(--color-divider-navy);
}

.enhancement-control:last-child {
  border-bottom: none;
}

.control-icon {
  color: var(--color-accent-primary);
  font-size: 1rem;
  width: 24px;
  text-align: center;
  flex-shrink: 0;
}

.enhancement-slider {
  flex: 1;
  height: 6px;
  background: var(--color-bg-primary);
  border-radius: 3px;
  outline: none;
  -webkit-appearance: none;
  appearance: none;
}

.enhancement-slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 16px;
  height: 16px;
  background: var(--color-accent-primary);
  cursor: pointer;
  border-radius: 50%;
  border: 2px solid var(--color-bg-primary);
}

.enhancement-slider::-moz-range-thumb {
  width: 16px;
  height: 16px;
  background: var(--color-accent-primary);
  cursor: pointer;
  border-radius: 50%;
  border: 2px solid var(--color-bg-primary);
}

.enhancement-slider:hover::-webkit-slider-thumb {
  background: var(--color-accent-hover);
}

.enhancement-slider:hover::-moz-range-thumb {
  background: var(--color-accent-hover);
}

.control-value {
  color: var(--color-text-secondary);
  font-size: 0.85rem;
  min-width: 50px;
  text-align: right;
  flex-shrink: 0;
}

/* Annotations section (migrated from global styles.css) */
.annotator-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.annotator-item {
  background-color: var(--color-bg-secondary);
  border: var(--border-width-standard) solid var(--color-border-secondary);
  border-radius: var(--border-radius-small);
  margin-bottom: 0.75rem;
  padding: 0.75rem;
}

.annotator-item:last-child {
  margin-bottom: 0rem;
}

.annotator-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  user-select: none;
}

.annotator-info {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.color-badge {
  width: 20px;
  height: 20px;
  border-radius: 4px;
  border: 2px solid var(--color-border-secondary);
  background-color: var(--color-bg-primary);
  cursor: pointer;
}

.annotator-name {
  color: var(--color-text-primary);
  font-weight: var(--font-weight-semibold);
  font-size: 0.95rem;
}

.annotator-info-text {
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
}

.annotator-role {
  font-size: 0.75rem;
  color: var(--color-text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.toggle-btn {
  background: none;
  border: none;
  color: var(--color-accent-primary);
  cursor: pointer;
  padding: 0.25rem;
}

.annotator-details {
  margin-top: 0.5rem;
  padding-left: 1.75rem;
}

.pathology-list {
  margin-top: 0.75rem;
  padding-left: 1.75rem;
}

.pathology-item {
  margin-bottom: 0.5rem;
}

.pathology-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.visibility-btn {
  background: none;
  border: none;
  color: var(--color-accent-primary);
  cursor: pointer;
  padding: 0.25rem;
}

.pathology-name {
  color: var(--color-text-primary);
  font-size: 0.9rem;
  flex: 1;
}

.stats-link {
  color: var(--color-text-secondary);
  text-decoration: none;
  font-size: 0.85rem;
}

.stats-link:hover {
  color: var(--color-accent-primary);
}

.stats-content {
  margin-top: 0.5rem;
  padding-left: 2rem;
  color: var(--color-text-secondary);
}

/* Clinical Comments Styles - Medical Record Aesthetic */
.comments-card {
  position: relative;
  overflow: hidden;
}

.comments-card::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image:
    linear-gradient(rgba(255, 193, 7, 0.02) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255, 193, 7, 0.02) 1px, transparent 1px);
  background-size: 20px 20px;
  pointer-events: none;
  opacity: 0.5;
}

.comment-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  height: 24px;
  padding: 0 6px;
  margin-left: auto;
  background: linear-gradient(135deg, var(--color-accent-primary), #d4a017);
  color: var(--color-bg-primary);
  font-size: 0.75rem;
  font-weight: var(--font-weight-bold);
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(255, 193, 7, 0.3);
}

.card-title.with-count {
  display: flex;
  align-items: center;
  width: 100%;
  gap: 0.5rem;
}

.comments-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  color: var(--color-text-secondary);
}

.no-comments {
  padding: 1.5rem;
  text-align: center;
  color: var(--color-text-secondary);
  border: 1px dashed var(--color-border-secondary);
  border-radius: var(--border-radius-small);
}

.comments-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  max-height: 500px;
  overflow-y: auto;
  padding-right: 4px;
}

.comments-list::-webkit-scrollbar {
  width: 4px;
}

.comments-list::-webkit-scrollbar-track {
  background: var(--color-scrollbar-track);
}

.comments-list::-webkit-scrollbar-thumb {
  background: var(--color-accent-primary);
  border-radius: 2px;
}

.comment-item {
  background: linear-gradient(135deg, rgba(12, 25, 48, 0.8), rgba(8, 20, 36, 0.9));
  border: 1px solid rgba(255, 193, 7, 0.15);
  border-radius: var(--border-radius-small);
  padding: 0.875rem 1rem;
  position: relative;
  transition: all 0.3s ease;
}

.comment-item:hover {
  border-color: rgba(255, 193, 7, 0.3);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
}

.comment-item.older {
  opacity: 0.85;
}

.comment-item.latest {
  border-color: rgba(255, 193, 7, 0.4);
  box-shadow: 0 4px 16px rgba(255, 193, 7, 0.1);
}

.comment-item.latest::before {
  content: "";
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 3px;
  background: linear-gradient(180deg, var(--color-accent-primary), #d4a017);
  border-radius: var(--border-radius-small) 0 0 var(--border-radius-small);
}

.comment-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 0.625rem;
  gap: 0.75rem;
}

.comment-user {
  display: flex;
  align-items: center;
  gap: 0.625rem;
}

.user-hexagon {
  width: 32px;
  height: 36px;
  background: linear-gradient(135deg, rgba(255, 193, 7, 0.2), rgba(212, 160, 23, 0.1));
  clip-path: polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%);
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: "Courier New", monospace;
  font-size: 0.7rem;
  font-weight: bold;
  color: var(--color-accent-primary);
  border: 1px solid rgba(255, 193, 7, 0.3);
  flex-shrink: 0;
}

.user-hexagon.latest {
  background: linear-gradient(135deg, var(--color-accent-primary), #d4a017);
  color: var(--color-bg-primary);
}

.user-info {
  display: flex;
  flex-direction: column;
}

.user-name {
  font-size: 0.85rem;
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-primary);
  line-height: 1.2;
}

.user-role {
  font-size: 0.7rem;
  color: var(--color-text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.comment-time {
  font-family: "Courier New", monospace;
  font-size: 0.7rem;
  color: var(--color-text-secondary);
  padding: 0.25rem 0.5rem;
  background: rgba(255, 193, 7, 0.08);
  border-radius: 4px;
  white-space: nowrap;
}

.comment-time.latest {
  background: rgba(255, 193, 7, 0.15);
  color: var(--color-accent-primary);
  font-weight: 600;
}

.comment-message {
  margin: 0 0 0.5rem 0;
  font-size: 0.875rem;
  line-height: 1.5;
  color: var(--color-text-primary);
  white-space: pre-wrap;
  word-break: break-word;
}

.comment-actions {
  display: flex;
  gap: 0.75rem;
  margin-top: 0.5rem;
  padding-top: 0.5rem;
  border-top: 1px solid rgba(255, 255, 255, 0.05);
}

.action-link {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.75rem;
  color: var(--color-text-secondary);
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  transition: all 0.2s ease;
}

.action-link:hover {
  color: var(--color-accent-primary);
  background: rgba(255, 193, 7, 0.1);
}

.action-link.delete:hover {
  color: #ef4444;
  background: rgba(239, 68, 68, 0.1);
}

.expand-comments-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  width: 100%;
  padding: 0.625rem;
  margin: 0.25rem 0;
  background: rgba(255, 193, 7, 0.05);
  border: 1px dashed rgba(255, 193, 7, 0.3);
  border-radius: var(--border-radius-small);
  color: var(--color-accent-primary);
  font-size: 0.8rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.expand-comments-btn:hover {
  background: rgba(255, 193, 7, 0.1);
  border-color: var(--color-accent-primary);
}

.expand-comments-btn.show-less {
  margin-top: 0.75rem;
}

/* Comment Edit Mode */
.comment-item.editing {
  border-color: var(--color-accent-primary);
  box-shadow: 0 0 0 1px var(--color-accent-primary);
}

.comment-edit-mode {
  margin-top: 0.5rem;
}

.comment-textarea {
  width: 100%;
  background: rgba(8, 20, 36, 0.8);
  border: 1px solid var(--color-border-secondary);
  border-radius: var(--border-radius-small);
  color: var(--color-text-primary);
  padding: 0.625rem 0.75rem;
  font-size: 0.875rem;
  line-height: 1.5;
  resize: vertical;
  font-family: inherit;
  transition: all 0.2s ease;
}

.comment-textarea:focus {
  outline: none;
  border-color: var(--color-accent-primary);
  box-shadow: 0 0 0 2px rgba(255, 193, 7, 0.1);
}

.comment-textarea.new-comment {
  background: rgba(8, 20, 36, 0.6);
  border-color: rgba(255, 193, 7, 0.2);
}

.comment-textarea.new-comment:focus {
  background: rgba(8, 20, 36, 0.9);
  border-color: var(--color-accent-primary);
}

.edit-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 0.5rem;
}

.action-buttons {
  display: flex;
  gap: 0.5rem;
}

.btn-cancel {
  padding: 0.375rem 0.875rem;
  background: transparent;
  border: 1px solid var(--color-border-secondary);
  border-radius: var(--border-radius-small);
  color: var(--color-text-secondary);
  font-size: 0.8rem;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-cancel:hover {
  border-color: var(--color-text-primary);
  color: var(--color-text-primary);
}

.btn-save {
  padding: 0.375rem 0.875rem;
  background: var(--color-accent-primary);
  border: none;
  border-radius: var(--border-radius-small);
  color: var(--color-bg-primary);
  font-size: 0.8rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.375rem;
}

.btn-save:hover:not(:disabled) {
  background: var(--color-accent-hover);
}

.btn-save:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.char-count {
  font-family: "Courier New", monospace;
  font-size: 0.7rem;
  color: var(--color-text-secondary);
}

/* Add Comment Section */
.add-comment-section {
  margin-top: 0.5rem;
  padding-top: 0.5rem;
}

.add-comment-divider::before,
.add-comment-divider::after {
  content: "";
  flex: 1;
  height: 1px;
  background: linear-gradient(90deg, transparent, var(--color-border-secondary), transparent);
}

.add-comment-divider span {
  padding: 0 1rem;
}

.add-comment-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 0.5rem;
}

.btn-submit-comment {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: linear-gradient(135deg, var(--color-accent-primary), #d4a017);
  border: none;
  border-radius: var(--border-radius-small);
  color: var(--color-bg-primary);
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(255, 193, 7, 0.3);
}

.btn-submit-comment:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(255, 193, 7, 0.4);
}

.btn-submit-comment:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
}
.report-text {
  white-space: pre-wrap;
  word-break: break-word;
  font-family: inherit;
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.85);
  background: rgba(12, 25, 48, 0.6);
  border: 1px solid rgba(255, 193, 7, 0.15);
  border-radius: var(--border-radius-small);
  padding: 0.875rem 1rem;
  margin: 0;
  max-height: 300px;
  overflow-y: auto;
}

.report-meta {
  color: rgba(255, 255, 255, 0.85);
}
</style>
